import { Formik } from 'formik';
import * as yup from 'yup';
import {
    Box, Typography, TextField, MenuItem,
    Checkbox, FormControlLabel, Button, Divider, IconButton,
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';


export const FIELD_TYPES = [
    { value: 'text',     label: 'Text' },
    { value: 'number',   label: 'Number' },
    { value: 'boolean',  label: 'Boolean' },
    { value: 'select',   label: 'Select (static)' },
    { value: 'array',    label: 'Array' },
    { value: 'date',     label: 'Date' },
    { value: 'textarea', label: 'Textarea' },
    { value: 'email',    label: 'Email' },
    { value: 'color',    label: 'Color' },
    { value: 'relation', label: 'Relation (API)' },
];

export const FIELD_INITIAL = {
    name: '', label: '', type: 'text', required: false,
    defaultValue: '', order: 0,
    minValue: '', maxValue: '', minLength: '', maxLength: '',
    options: [],
    optionsFrom: '', optionsValue: '', optionsLabel: '',
    showInGrid: true, gridFlex: 1, gridAlign: 'left',
    showInForm: true, formSpan: 6, readOnly: false,
    searchable: false,
};

const fieldSchema = yup.object().shape({
    name:  yup.string().required('Required').matches(/^[a-zA-Z_][a-zA-Z0-9_.]*$/, 'Valid JS identifier'),
    label: yup.string().required('Required'),
    type:  yup.string().required('Required'),
});

const OptionsEditor = ({ options = [], onChange }) => {
    const addOption    = ()           => onChange([...options, { value: '', label: '' }]);
    const updateOption = (i, key, val) => onChange(options.map((o, idx) => idx === i ? { ...o, [key]: val } : o));
    const removeOption = (i)          => onChange(options.filter((_, idx) => idx !== i));

    return (
        <Box sx={{ gridColumn: 'span 12' }}>
            <Typography variant="caption" color="text.secondary" mb={0.5} display="block">
                Static Options
            </Typography>
            {options.map((opt, i) => (
                <Box key={i} display="flex" gap={1} mb={0.5} alignItems="center">
                    <TextField
                        size="small" label="Value" value={opt.value} variant="outlined"
                        onChange={e => updateOption(i, 'value', e.target.value)}
                        sx={{ flex: 1 }}
                    />
                    <TextField
                        size="small" label="Label" value={opt.label} variant="outlined"
                        onChange={e => updateOption(i, 'label', e.target.value)}
                        sx={{ flex: 1 }}
                    />
                    <IconButton size="small" color="error" onClick={() => removeOption(i)}>
                        <Delete fontSize="small" />
                    </IconButton>
                </Box>
            ))}
            <Button size="small" onClick={addOption} startIcon={<Add />}>Add Option</Button>
        </Box>
    );
};

const FieldDefForm = ({ initialValues, onSubmit }) => (
    <Formik enableReinitialize initialValues={initialValues} validationSchema={fieldSchema} onSubmit={onSubmit}>
        {({ values, errors, touched, handleChange, handleBlur, handleSubmit: fSubmit, setFieldValue }) => {
            const needsStaticOptions = ['select', 'array'].includes(values.type);
            const needsRelation      = values.type === 'relation';
            const needsNumValidation = values.type === 'number';
            const needsStrValidation = ['text', 'email', 'textarea'].includes(values.type);

            return (
                <form onSubmit={fSubmit}>
                    <Box display="grid" gridTemplateColumns="repeat(12, minmax(0, 1fr))" gap={2}>

                        <TextField label="Field Name" name="name" size="small" variant="filled" required
                            value={values.name} onChange={handleChange} onBlur={handleBlur}
                            error={!!touched.name && !!errors.name}
                            helperText={(touched.name && errors.name) || 'camelCase or snake_case'}
                            sx={{ gridColumn: 'span 6' }}
                        />
                        <TextField label="Label" name="label" size="small" variant="filled" required
                            value={values.label} onChange={handleChange} onBlur={handleBlur}
                            error={!!touched.label && !!errors.label}
                            helperText={touched.label && errors.label}
                            sx={{ gridColumn: 'span 6' }}
                        />
                        <TextField label="Type" name="type" size="small" variant="filled" required select
                            value={values.type} onChange={handleChange}
                            sx={{ gridColumn: 'span 6' }}
                        >
                            {FIELD_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                        </TextField>
                        <TextField label="Order" name="order" size="small" variant="filled" type="number"
                            value={values.order} onChange={handleChange}
                            slotProps={{ htmlInput: { min: 0 } }}
                            sx={{ gridColumn: 'span 3' }}
                        />
                        <TextField label="Default Value" name="defaultValue" size="small" variant="filled"
                            value={values.defaultValue ?? ''} onChange={handleChange}
                            sx={{ gridColumn: 'span 3' }}
                        />

                        <Divider sx={{ gridColumn: 'span 12' }} />

                        {needsStrValidation && (<>
                            <TextField label="Min Length" name="minLength" size="small" variant="filled" type="number"
                                value={values.minLength ?? ''} onChange={handleChange}
                                slotProps={{ htmlInput: { min: 0 } }}
                                sx={{ gridColumn: 'span 6' }}
                            />
                            <TextField label="Max Length" name="maxLength" size="small" variant="filled" type="number"
                                value={values.maxLength ?? ''} onChange={handleChange}
                                slotProps={{ htmlInput: { min: 0 } }}
                                sx={{ gridColumn: 'span 6' }}
                            />
                        </>)}
                        {needsNumValidation && (<>
                            <TextField label="Min Value" name="minValue" size="small" variant="filled" type="number"
                                value={values.minValue ?? ''} onChange={handleChange}
                                sx={{ gridColumn: 'span 6' }}
                            />
                            <TextField label="Max Value" name="maxValue" size="small" variant="filled" type="number"
                                value={values.maxValue ?? ''} onChange={handleChange}
                                sx={{ gridColumn: 'span 6' }}
                            />
                        </>)}

                        {needsStaticOptions && (
                            <OptionsEditor
                                options={values.options}
                                onChange={opts => setFieldValue('options', opts)}
                            />
                        )}

                        {needsRelation && (<>
                            <TextField label="Options From (API endpoint)" name="optionsFrom" size="small" variant="filled"
                                value={values.optionsFrom} onChange={handleChange}
                                helperText='e.g. "/airports"'
                                sx={{ gridColumn: 'span 4' }}
                            />
                            <TextField label="Value Key" name="optionsValue" size="small" variant="filled"
                                value={values.optionsValue} onChange={handleChange}
                                helperText='e.g. "_id"'
                                sx={{ gridColumn: 'span 4' }}
                            />
                            <TextField label="Label Key" name="optionsLabel" size="small" variant="filled"
                                value={values.optionsLabel} onChange={handleChange}
                                helperText='e.g. "name"'
                                sx={{ gridColumn: 'span 4' }}
                            />
                        </>)}

                        <Divider sx={{ gridColumn: 'span 12' }} />

                        <Box sx={{ gridColumn: 'span 12', display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                            <FormControlLabel label="Show in Grid"
                                control={<Checkbox name="showInGrid" checked={values.showInGrid} onChange={handleChange} />}
                            />
                            <FormControlLabel label="Show in Form"
                                control={<Checkbox name="showInForm" checked={values.showInForm} onChange={handleChange} />}
                            />
                            <FormControlLabel label="Required"
                                control={<Checkbox name="required" checked={values.required} onChange={handleChange} />}
                            />
                            <FormControlLabel label="Read Only"
                                control={<Checkbox name="readOnly" checked={values.readOnly} onChange={handleChange} />}
                            />
                            <FormControlLabel label="Searchable"
                                control={<Checkbox name="searchable" checked={values.searchable} onChange={handleChange} />}
                            />
                        </Box>

                        <TextField label="Grid Flex" name="gridFlex" size="small" variant="filled" type="number"
                            value={values.gridFlex} onChange={handleChange}
                            slotProps={{ htmlInput: { min: 0.5, step: 0.5 } }}
                            sx={{ gridColumn: 'span 3' }}
                        />
                        <TextField label="Grid Align" name="gridAlign" size="small" variant="filled" select
                            value={values.gridAlign} onChange={handleChange}
                            sx={{ gridColumn: 'span 3' }}
                        >
                            {['left', 'center', 'right'].map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
                        </TextField>
                        <TextField label="Form Span (1-12)" name="formSpan" size="small" variant="filled" type="number"
                            value={values.formSpan} onChange={handleChange}
                            slotProps={{ htmlInput: { min: 1, max: 12 } }}
                            sx={{ gridColumn: 'span 3' }}
                        />

                        <Button fullWidth type="submit" variant="contained" color="secondary"
                            sx={{ gridColumn: 'span 12', height: 42 }}
                        >
                            Save Field
                        </Button>
                    </Box>
                </form>
            );
        }}
    </Formik>
);

export default FieldDefForm;
